import React from 'react';
import moment from 'moment';
import {
  DRIVER_STATUS_ACTIVE,
  JOB_ORDER_STATUS_ACCEPTED,
  JOB_ORDER_STATUS_ASSIGNED,
  JOB_ORDER_STATUS_CREATED,
  JOB_STATUS_CANCELLED,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_FAILED,
  getJobTypeToActionMapping,
} from 'common/jobConstants';

import lodashCompact from 'lodash/fp/compact';
import SrText from 'client/components/SrText';
import SrSimpleDropdown, { SR_SIMPLE_DROPDOWN_THEME_V2 } from 'client/components/SrSimpleDropdown';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';
import {
  SR_API_ACTION_JOB_ORDER_ASSIGN_DRIVER,
  SR_API_ACTION_JOB_ORDER_UNASSIGN_DRIVER,
} from 'api/actionTypes';

import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';

import './SrSunSetupOrdersOverview.less';

const noDriver = {
  id: null,
  value: null,
  title: 'No Driver',
  status: DRIVER_STATUS_ACTIVE,
};

const SrSunSetupOrderItem = ({ listing, drivers, reloadDataTable }) => {
  const dispatch = useDispatch();

  const _getDriverOptions = () => {
    const options = [noDriver];
    for (const driver of drivers) {
      options.push({
        id: driver.id,
        value: driver.id,
        title: driver.name,
        status: driver.status,
      });
    }
    return options;
  };

  const _parseJobTypeNames = (jobs) => {
    if (!Array.isArray(jobs) || jobs.length === 0) {
      return 'Missing Jobs';
    }

    return jobs.map((job) => getJobTypeToActionMapping({ job, listing })).join(', ');
  };

  const _getPromiseDateColor = (promiseDate) => {
    const redThreshold = moment(promiseDate).subtract(1, 'day');
    const orangeThreshold = redThreshold.subtract(2, 'days');
    const now = moment();

    if (now.isAfter(redThreshold)) {
      return 'red';
    }
    if (now.isAfter(orangeThreshold)) {
      return 'orange300';
    }
    return 'green';
  };

  const _handleDriverChange = async ({ jobOrderId, driverId }) => {
    if (!driverId) {
      await dispatch(postWithState, {
        action: SR_API_ACTION_JOB_ORDER_UNASSIGN_DRIVER,
        payload: {
          jobOrderId,
          driverId,
        },
      });
    } else {
      await dispatch(postWithState, {
        action: SR_API_ACTION_JOB_ORDER_ASSIGN_DRIVER,
        payload: {
          jobOrderId,
          driverId,
        },
      });
    }
    await reloadDataTable(listing.id);
  };

  const _getStatusColor = (status) => {
    switch (status) {
      case JOB_STATUS_CANCELLED:
      case JOB_STATUS_FAILED:
        return 'red';
      case JOB_STATUS_COMPLETED:
        return 'green';
      default:
        return 'orange300';
    }
  };

  const _getOrderLink = (jobOrder) => {
    return `/sunsetup/listing-orders/${jobOrder.listingId}`;
  };

  const _canAssignDriver = (jobOrder) => {
    switch (jobOrder.status) {
      case JOB_ORDER_STATUS_CREATED:
      case JOB_ORDER_STATUS_ASSIGNED:
      case JOB_ORDER_STATUS_ACCEPTED:
        return true;
      default:
        return false;
    }
  };

  const _formatDate = (momentDate) => {
    return moment(momentDate).format('MMM DD');
  };

  const driversInMarket = _getDriverOptions();

  return (
    <tr key={listing.id} styleName="order">
      <td styleName="orderCol">
        <SrText
          font="greycliff"
          weight="700"
          fontSize="14px"
          color={_getPromiseDateColor(listing.promiseDate)}
        >
          {_formatDate(listing.promiseDate)}
        </SrText>
      </td>
      <td styleName="orderCol">
        <SrLink to={`/admin/listings/edit/${listing.id}`}>{listing.addressStreetDisplay}</SrLink>
      </td>
      <td styleName="markets orderCol">{listing.marketDisplayName}</td>
      <td styleName="partners orderCol">
        <SrLink to={`/partner/${listing.partnerSlug}/listing/${listing.id}`}>
          {listing.partnerDisplayName}
        </SrLink>
      </td>
      <td styleName="drivers orderCol">
        {(listing.jobOrders || []).map((jobOrder) => {
          const driver =
            driversInMarket.find((driver) => jobOrder.driverId === driver.value) || noDriver;
          const canAssignDriver = _canAssignDriver(jobOrder);
          const driverOptions = lodashCompact(
            driversInMarket.map((option) => {
              if (option.id === driver.id) {
                return {
                  ...option,
                  isActive: option.id === jobOrder.driverId,
                };
              }
              return option.status === DRIVER_STATUS_ACTIVE ? option : null;
            }),
          );

          return (
            <tr styleName="assignDriver" key={`${jobOrder.id}_driver`}>
              <td styleName="subRow">
                {canAssignDriver ? (
                  <SrBox margin="0" padding="0">
                    <SrSimpleDropdown
                      theme={SR_SIMPLE_DROPDOWN_THEME_V2}
                      title={driver.title}
                      options={driverOptions}
                      onChange={(driverId) =>
                        _handleDriverChange({ driverId, jobOrderId: jobOrder.id })
                      }
                    />
                  </SrBox>
                ) : (
                  driver.title
                )}
              </td>
            </tr>
          );
        })}
      </td>
      <td styleName="orderCol">
        {(listing.jobOrders || []).map((jobOrder) => (
          <tr key={`${jobOrder.id}_status`}>
            <td styleName="subRow">
              <SrText
                color={_getStatusColor(jobOrder.status)}
                font="greycliff"
                weight="500"
                fontSize="14px"
              >
                {jobOrder.status}
              </SrText>
            </td>
          </tr>
        ))}
      </td>
      <td styleName="dueDates orderCol">
        {(listing.jobOrders || []).map((jobOrder) => (
          <tr key={`${jobOrder.id}_dueDates`}>
            <td styleName="subRow">
              <SrText font="greycliff" weight="500" fontSize="14px">
                {_formatDate(jobOrder.dueDate)}
              </SrText>
            </td>
          </tr>
        ))}
      </td>
      <td styleName="jobs orderCol">
        {(listing.jobOrders || []).map(({ jobs }) => (
          <tr key={`${jobs[0]?.jobOrderId}_jobs`}>
            <td styleName="subRow">
              <SrText font="greycliff" weight="500" fontSize="14px" lineHeight="15px">
                {_parseJobTypeNames(jobs)}
              </SrText>
            </td>
          </tr>
        ))}
      </td>
      <td styleName="orderCol">
        {(listing.jobOrders || []).map((jobOrder) => (
          <tr key={`${jobOrder.id}_orderId`}>
            <td styleName="subRow">
              <SrLink to={_getOrderLink(jobOrder)} color="blue300">
                {jobOrder.id}
              </SrLink>
            </td>
          </tr>
        ))}
      </td>
    </tr>
  );
};

export default SrSunSetupOrderItem;
